import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Sparkles, Wifi, Smartphone, Users, Heart, Music, Building2,
  GraduationCap, Rocket, Star, Shield, Zap, Eye, Mic2, Trophy,
  Truck, Gift, ShieldCheck, MessageCircle, Settings2, FileText,
  Ticket, Share2, Images, Wallet, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLang } from '../i18n/LanguageContext';
import ColorSimulator from '../components/ColorSimulator';
import HeroVideo from '../components/HeroVideo';
import LangSuggestBanner from '../components/LangSuggestBanner';
import { localizeKitName } from '../lib/productName';
import { usePricing } from '../lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BRACELET_IMG = '/kit-product.png';
// Les suggestions de logos sont adaptées au marché/langue du visiteur
const MARKET_BY_LANG = { fr: 'fr', it: 'it', en: 'ae', ar: 'ae' };

const THEME_TITLES = {
  festivals: { fr: 'Festivals & concerts', en: 'Festivals & concerts', it: 'Festival e concerti', ar: 'المهرجانات والحفلات' },
  sport:     { fr: 'Sport', en: 'Sport', it: 'Sport', ar: 'الرياضة' },
  corporate: { fr: 'Entreprises & collectivités', en: 'Companies & institutions', it: 'Aziende ed enti', ar: 'الشركات والمؤسسات' },
  brands:    { fr: 'Bars & marques', en: 'Brands & venues', it: 'Bar e marchi', ar: 'العلامات التجارية والأماكن' },
  weddings:  { fr: 'Mariages & événements privés', en: 'Weddings & private events', it: 'Matrimoni ed eventi privati', ar: 'حفلات الزفاف والمناسبات الخاصة' },
};

const MARKET_THEMES = {
  fr: [
    { key: 'festivals', eventKey: 'festival', items: [
      { name: 'Tomorrowland', file: 'ev-tomorrowland.webp' }, { name: 'Coachella', file: 'ev-coachella.webp' },
      { name: 'Hellfest', file: 'ev-hellfest.webp' }, { name: 'Rock en Seine', file: 'ev-rockenseine.webp' },
      { name: 'Dour Festival', file: 'ev-dour.webp' }, { name: 'La terrasse FMR', file: 'ex-fmr.webp' },
    ]},
    { key: 'sport', eventKey: 'sport', items: [
      { name: 'Betclic', file: 'ex-betclic.webp' }, { name: 'Alpine', file: 'ex-alpine.webp' },
      { name: 'Paris', file: 'ev-psg.webp' }, { name: 'Decathlon', file: 'ev-decathlon.webp' },
      { name: 'Roland-Garros', file: 'ev-rg.webp' }, { name: 'Tour de France', file: 'ev-tdf.webp' },
      { name: 'Paris 2026', file: 'ev-games.webp' }, { name: 'Basic-Fit', file: 'ev-basicfit.webp' },
    ]},
    { key: 'corporate', eventKey: 'entreprise', items: [
      { name: 'Orange', file: 'ev-orange.webp' }, { name: "L'Oréal", file: 'ev-loreal.webp' },
      { name: 'Capgemini', file: 'ev-capgemini.webp' }, { name: 'Renault', file: 'ev-renault.webp' },
      { name: 'TotalEnergies', file: 'ev-total.webp' }, { name: 'Google', file: 'ev-google.webp' },
      { name: 'Summit 2026', file: 'ev-summit.webp' }, { name: 'NesteNN', file: 'ex-nestenn.webp' },
      { name: 'Matawan', file: 'ex-matawan.webp' }, { name: 'Connecte Châlons', file: 'ex-chalons.webp' },
    ]},
    { key: 'brands', eventKey: 'entreprise', items: [
      { name: 'Aperol', file: 'ex-aperol.webp' }, { name: 'Red Bull', file: 'ev-redbull.webp' },
      { name: 'Heineken', file: 'ev-heineken.webp' }, { name: 'Desperados', file: 'ev-desperados.webp' },
      { name: 'Coca-Cola', file: 'ev-coca.webp' }, { name: 'Corona', file: 'ev-corona.webp' },
      { name: 'Bacardi', file: 'ev-bacardi.webp' }, { name: 'Bud', file: 'ex-bud.webp' },
    ]},
    { key: 'weddings', eventKey: 'mariage', items: [
      { name: 'Sarah & Thomas', file: 'ev-mariage.webp' }, { name: 'Julie & Max', file: 'ev-juliemax.webp' },
      { name: 'Emma & Lucas', file: 'ev-emmalucas.webp' }, { name: 'Léa & Hugo', file: 'ev-leahugo.webp' },
      { name: 'Camille & Nina', file: 'ev-camillenina.webp' },
    ]},
  ],
  it: [
    { key: 'festivals', eventKey: 'festival', items: [
      { name: 'Jova Beach', file: 'it-jova.webp' }, { name: 'Umbria Jazz', file: 'it-umbriajazz.webp' },
      { name: 'Lucca Comics', file: 'it-lucca.webp' }, { name: 'Tomorrowland', file: 'ev-tomorrowland.webp' },
      { name: 'Ultra', file: 'ev-ultra.webp' },
    ]},
    { key: 'sport', eventKey: 'sport', items: [
      { name: 'Juventus', file: 'it-juve.webp' }, { name: 'Inter', file: 'it-inter.webp' },
      { name: 'Ferrari', file: 'it-ferrari.webp' }, { name: 'Maratona 2026', file: 'ev-marathon.webp' },
      { name: 'Running Club', file: 'ev-nike.webp' },
    ]},
    { key: 'corporate', eventKey: 'entreprise', items: [
      { name: 'Lavazza', file: 'it-lavazza.webp' }, { name: 'TIM', file: 'it-tim.webp' },
      { name: 'Google', file: 'ev-google.webp' }, { name: 'TotalEnergies', file: 'ev-total.webp' },
      { name: 'Summit 2026', file: 'ev-summit.webp' }, { name: 'Seminario 2026', file: 'ev-seminaire.webp' },
    ]},
    { key: 'brands', eventKey: 'entreprise', items: [
      { name: 'Campari', file: 'it-campari.webp' }, { name: 'Aperol', file: 'ex-aperol.webp' },
      { name: 'Red Bull', file: 'ev-redbull.webp' }, { name: 'Heineken', file: 'ev-heineken.webp' },
      { name: 'Coca-Cola', file: 'ev-coca.webp' }, { name: 'Corona', file: 'ev-corona.webp' },
    ]},
    { key: 'weddings', eventKey: 'mariage', items: [
      { name: 'Giulia & Marco', file: 'it-giuliamarco.webp' }, { name: 'Emma & Lucas', file: 'ev-emmalucas.webp' },
      { name: 'Sarah & Thomas', file: 'ev-mariage.webp' },
    ]},
  ],
  ae: [
    { key: 'festivals', eventKey: 'festival', items: [
      { name: 'Coca-Cola Arena', file: 'ae-ccarena.webp' }, { name: 'Global Village', file: 'ae-globalvillage.webp' },
      { name: 'Expo City Dubai', file: 'ae-expo.webp' },
    ]},
    { key: 'sport', eventKey: 'sport', items: [
      { name: 'Dubai Run', file: 'ae-dubairun.webp' }, { name: 'Marathon 2026', file: 'ev-marathon.webp' },
      { name: 'Running Club', file: 'ev-nike.webp' },
    ]},
    { key: 'corporate', eventKey: 'entreprise', items: [
      { name: 'Careem', file: 'ae-careem.webp' }, { name: 'Emirates', file: 'ae-emirates.webp' },
      { name: 'e& etisalat', file: 'ae-etisalat.webp' }, { name: 'Emaar', file: 'ae-emaar.webp' },
      { name: 'noon', file: 'ae-noon.webp' }, { name: 'ADNOC', file: 'ae-adnoc.webp' },
    ]},
    { key: 'brands', eventKey: 'entreprise', items: [
      { name: 'Atlantis', file: 'ae-atlantis.webp' }, { name: 'Red Bull', file: 'ev-redbull.webp' },
      { name: 'Coca-Cola', file: 'ev-coca.webp' }, { name: 'Corona', file: 'ev-corona.webp' },
    ]},
    { key: 'weddings', eventKey: 'mariage', items: [
      { name: 'Fatima & Omar', file: 'ae-fatimaomar.webp' }, { name: 'Layla & Ahmed', file: 'ae-laylaahmed.webp' },
      { name: 'Sarah & Thomas', file: 'ev-mariage.webp' },
    ]},
  ],
};
const SOCIAL = {
  fr: { badge: 'Ils ont illuminé leur événement', title: 'De vrais événements, de vrais bracelets', intro: "Mariages, festivals, soirées d'entreprise : un aperçu de nos animations lumineuses en conditions réelles.", cta: 'Voir toute la galerie' },
  en: { badge: 'They lit up their event', title: 'Real events, real wristbands', intro: 'Weddings, festivals, corporate parties: a glimpse of our light shows in real conditions.', cta: 'See the full gallery' },
  ar: { badge: 'أناروا فعالياتهم', title: 'فعاليات حقيقية، أساور حقيقية', intro: 'حفلات الزفاف والمهرجانات وحفلات الشركات: لمحة عن عروضنا الضوئية في ظروف حقيقية.', cta: 'شاهد المعرض كاملاً' },
  it: { badge: 'Hanno illuminato il loro evento', title: 'Eventi veri, bracciali veri', intro: 'Matrimoni, festival, feste aziendali: uno sguardo alle nostre animazioni luminose in condizioni reali.', cta: 'Vedi tutta la galleria' },
};
const REVIEWS_COPY = {
  fr: { badge: 'Ils nous ont fait confiance', title: 'Ce que disent nos clients', based: 'avis vérifiés', leave: 'Laisser un avis' },
  en: { badge: 'They trusted us', title: 'What our clients say', based: 'verified reviews', leave: 'Leave a review' },
  ar: { badge: 'وثقوا بنا', title: 'ماذا يقول عملاؤنا', based: 'تقييمات موثّقة', leave: 'اكتب تقييماً' },
  it: { badge: 'Si sono fidati di noi', title: 'Cosa dicono i nostri clienti', based: 'recensioni verificate', leave: 'Lascia una recensione' },
};
const resolveMediaUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);
const mediaTitle = (item, lang) => (
  (lang === 'en' && item.titleEn) || (lang === 'ar' && item.titleAr) || (lang === 'it' && item.titleIt) || item.title || ''
);
const IMMERSION_IMG = 'https://images.unsplash.com/photo-1671432403854-cbe798eb44fb?auto=format&fit=crop&q=80&w=1920';

const NEW = {
  fr: {
    subtitle: "Illuminez votre événement avec nos bracelets LED synchronisés ! Une ambiance immersive clé en main : contrôleur inclus, personnalisation à votre image et fonction connectée et interactive pour offrir une expérience unique à vos invités. Formules de 50 à 1 000 personnes (et sur-mesure au-delà).",
    priceFrom: 'Kits complets à partir de', ht: 'HT',
    ctaQuote: 'Demander un devis gratuit', ctaCatalogue: 'Voir les kits', ctaWhatsapp: 'Devis rapide WhatsApp',
    trust: ['Expédition rapide dans le monde entier', 'Prêt à l\'emploi, sans compétence technique', 'Contrôleur inclus', 'Marquage & lien NFC offerts'],
    bestBadge: 'Prêts à l\'emploi', bestTitle: 'Nos kits les plus demandés', bestCta: 'Voir tous les kits', from: 'dès',
    nfcBadge: 'La puce NFC intégrée',
    nfcTitle: 'Un bracelet, plein de pouvoirs',
    nfcIntro: 'Au-delà de la lumière, chaque bracelet s\'adapte à votre événement grâce à sa puce NFC.',
    marquage: 'Offerts : le marquage 1 couleur au nom de votre événement et le lien URL intégré au bracelet.',
    marquageExample: 'Exemples réels : le nom ou le logo de votre événement imprimé sur chaque bracelet. La puce NFC (à droite) ouvre le lien de votre choix.',
    nfcItems: [
      { title: 'Billet d\'entrée', desc: 'Scanné à l\'arrivée, fini les tickets papier.' },
      { title: 'Accès prioritaire', desc: 'VIP, backstage ou carré selon le bracelet.' },
      { title: 'Vos réseaux sociaux', desc: 'Un tap ouvre votre Instagram, TikTok ou site.' },
      { title: 'Galerie partagée', desc: 'Mariage, anniversaire : vos invités déposent et récupèrent toutes les photos et vidéos au même endroit.' },
      { title: 'Porte-monnaie / cashless', desc: 'Vos invités règlent leurs consommations au bar ou au festival d\'un simple geste.' },
      { title: 'Appli ou boutique', desc: 'Lien direct pour télécharger une application ou acheter le produit (ex. page Amazon).' },
    ],
  },
  en: {
    subtitle: 'Light up your event with our synchronised LED wristbands! A turnkey immersive atmosphere: controller included, personalised to your brand, plus a connected, interactive feature for a unique guest experience. Packages from 50 to 1,000 guests (and bespoke beyond).',
    priceFrom: 'Complete kits from', ht: 'excl. VAT',
    ctaQuote: 'Request a free quote', ctaCatalogue: 'See the kits', ctaWhatsapp: 'Quick quote on WhatsApp',
    trust: ['Fast worldwide shipping', 'Ready to use, no tech skills needed', 'Controller included', 'Branding & NFC link free'],
    bestBadge: 'Ready to use', bestTitle: 'Our most requested kits', bestCta: 'See all kits', from: 'from',
    nfcBadge: 'Built-in NFC chip',
    nfcTitle: 'One wristband, many powers',
    nfcIntro: 'Beyond the light, each wristband adapts to your event thanks to its NFC chip.',
    marquage: 'Free: 1-colour branding with your event name and the URL link built into the wristband.',
    marquageExample: 'Real examples: your event name or logo printed on each wristband. The NFC chip (on the right) opens the link of your choice.',
    nfcItems: [
      { title: 'Entry ticket', desc: 'Scanned on arrival — no more paper tickets.' },
      { title: 'Priority access', desc: 'VIP, backstage or front row per wristband.' },
      { title: 'Your social networks', desc: 'One tap opens your Instagram, TikTok or website.' },
      { title: 'Shared gallery', desc: 'Weddings, birthdays: guests upload and grab all photos and videos in one place.' },
      { title: 'Cashless wallet', desc: 'Guests pay for drinks or festival purchases with a single tap.' },
      { title: 'App or store', desc: 'Direct link to download an app or buy the product (e.g. an Amazon page).' },
    ],
  },
  ar: {
    subtitle: 'أضئ فعاليتك بأساورنا المضيئة LED المتزامنة! أجواء غامرة جاهزة للاستخدام: وحدة تحكم مشمولة، تخصيص بعلامتك، ووظيفة متصلة وتفاعلية لتجربة فريدة لضيوفك. باقات من 50 إلى 1000 شخص (وحسب الطلب لما فوق ذلك).',
    priceFrom: 'أطقم كاملة ابتداءً من', ht: 'بدون ضريبة',
    ctaQuote: 'اطلب عرض سعر مجاني', ctaCatalogue: 'شاهد الأطقم', ctaWhatsapp: 'عرض سعر سريع واتساب',
    trust: ['شحن سريع إلى جميع أنحاء العالم', 'جاهز للاستخدام دون مهارات تقنية', 'وحدة تحكم مشمولة', 'الطباعة والرابط NFC مجاناً'],
    bestBadge: 'جاهزة للاستخدام', bestTitle: 'أكثر أطقمنا طلباً', bestCta: 'عرض كل الأطقم', from: 'من',
    nfcBadge: 'شريحة NFC المدمجة',
    nfcTitle: 'سوار واحد بإمكانات متعددة',
    nfcIntro: 'إلى جانب الإضاءة، يتكيّف كل سوار مع فعاليتك بفضل شريحة NFC.',
    marquage: 'مجاناً: الطباعة بلون واحد باسم فعاليتك والرابط URL المدمج في السوار.',
    marquageExample: 'أمثلة حقيقية: اسم أو شعار فعاليتك مطبوع على كل سوار. شريحة NFC (على اليمين) تفتح الرابط الذي تختاره.',
    nfcItems: [
      { title: 'تذكرة الدخول', desc: 'تُمسح عند الوصول، لا مزيد من التذاكر الورقية.' },
      { title: 'دخول أولوية', desc: 'VIP أو كواليس أو الصف الأمامي حسب السوار.' },
      { title: 'شبكاتك الاجتماعية', desc: 'نقرة واحدة تفتح إنستغرام أو تيك توك أو موقعك.' },
      { title: 'معرض مشترك', desc: 'الأعراس وأعياد الميلاد: يرفع ضيوفك ويحصلون على كل الصور والفيديوهات في مكان واحد.' },
      { title: 'محفظة كاش-لِس', desc: 'يدفع ضيوفك مشترياتهم في البار أو المهرجان بلمسة واحدة.' },
      { title: 'تطبيق أو متجر', desc: 'رابط مباشر لتحميل تطبيق أو شراء المنتج (مثل صفحة أمازون).' },
    ],
  },
  it: {
    subtitle: "Illumina il tuo evento con i nostri bracciali LED sincronizzati! Un'atmosfera immersiva chiavi in mano: controller incluso, personalizzazione con il tuo marchio e funzione connessa e interattiva per offrire un'esperienza unica ai tuoi ospiti. Formule da 50 a 1.000 persone (e su misura oltre).",
    priceFrom: 'Kit completi a partire da', ht: 'IVA escl.',
    ctaQuote: 'Richiedi un preventivo gratuito', ctaCatalogue: 'Vedi i kit', ctaWhatsapp: 'Preventivo rapido su WhatsApp',
    trust: ['Spedizione rapida in tutto il mondo', "Pronto all'uso, senza competenze tecniche", 'Controller incluso', 'Stampa e link NFC in omaggio'],
    bestBadge: "Pronti all'uso", bestTitle: 'I nostri kit più richiesti', bestCta: 'Vedi tutti i kit', from: 'da',
    nfcBadge: 'Il chip NFC integrato',
    nfcTitle: 'Un bracciale, mille possibilità',
    nfcIntro: 'Oltre alla luce, ogni bracciale si adatta al tuo evento grazie al suo chip NFC.',
    marquage: "In omaggio: la stampa a 1 colore con il nome del tuo evento e il link URL integrato nel bracciale.",
    marquageExample: 'Esempi reali: il nome o il logo del tuo evento stampato su ogni bracciale. Il chip NFC (a destra) apre il link che preferisci.',
    nfcItems: [
      { title: "Biglietto d'ingresso", desc: "Scansionato all'arrivo, niente più biglietti cartacei." },
      { title: 'Accesso prioritario', desc: 'VIP, backstage o prima fila a seconda del bracciale.' },
      { title: 'I tuoi social', desc: 'Un tap apre il tuo Instagram, TikTok o sito web.' },
      { title: 'Galleria condivisa', desc: 'Matrimoni, compleanni: gli ospiti caricano e recuperano tutte le foto e i video in un unico posto.' },
      { title: 'Portafoglio cashless', desc: 'Gli ospiti pagano le consumazioni al bar o al festival con un semplice gesto.' },
      { title: 'App o negozio', desc: "Link diretto per scaricare un'app o acquistare il prodotto (es. pagina Amazon)." },
    ],
  },
};

const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section id={id} ref={ref}
      initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }} className={className}>
      {children}
    </motion.section>
  );
};

const StepCard = ({ number, icon: Icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="glass-card rounded-2xl p-8 text-center hover:-translate-y-2 transition-transform duration-300"
      data-testid={`step-card-${number}`}>
      <div className="w-14 h-14 rounded-full bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mx-auto mb-6">
        <Icon className="w-6 h-6 text-purple-400" />
      </div>
      <span className="text-sm tracking-widest uppercase text-purple-400 font-body mb-3 block">Étape {number}</span>
      <h3 className="font-heading text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed font-body">{description}</p>
    </motion.div>
  );
};

const USECASE_GALLERY = ['mariage', 'club', 'club', 'festival', 'club', 'corporate', 'corporate', 'corporate', 'concert', 'sport'];
const UseCaseCard = ({ icon: Icon, title, description, delay, galleryCategory, label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      className="glass-card rounded-2xl p-6 group hover:-translate-y-1 transition-all duration-300">
      <Link to={`/galerie?categorie=${galleryCategory || 'all'}`} className="block cursor-pointer">
        <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center mb-4 group-hover:bg-purple-600/20 transition-colors">
          <Icon className="w-5 h-5 text-purple-400" />
        </div>
        <h4 className="font-heading text-base font-bold text-white mb-2">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed font-body">{description}</p>
        <span className="inline-block mt-3 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">{label} →</span>
      </Link>
    </motion.div>
  );
};

const BenefitItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="w-5 h-5 text-purple-400" />
    </div>
    <div>
      <h4 className="font-heading text-base font-bold text-white mb-1">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed font-body">{description}</p>
    </div>
  </div>
);

const HomeV2 = () => {
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const { t, lang } = useLang();
  const L = NEW[lang] || NEW.fr;
  const market = MARKET_BY_LANG[lang] || 'fr';
  const titleLang = market === 'it' ? 'it' : (lang === 'ar' ? 'ar' : market === 'ae' ? 'en' : 'fr');
  const galleryThemes = MARKET_THEMES[market] || MARKET_THEMES.fr;
  const pricing = usePricing(lang);
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [realPhotos, setRealPhotos] = useState([]);

  const WA_NUMBERS = { fr: '33767735161', it: '33767735161', en: '971585070177', ar: '971585070177' };
  const whatsapp = `https://wa.me/${WA_NUMBERS[lang] || '33767735161'}`;
  const trustIcons = [Truck, Settings2, Wifi, Gift];
  const scrollToNfc = (e) => {
    if (e) e.preventDefault();
    const el = document.getElementById('nfc-powers');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowHeroVideo(true), 3000);
    fetch(`${BACKEND_URL}/api/public/reviews`).then((r) => (r.ok ? r.json() : [])).then((d) => setReviews(Array.isArray(d) ? d : [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/public/products`).then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/public/media?type=photo`).then((r) => setRealPhotos(Array.isArray(r.data) ? r.data.slice(0, 8) : [])).catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  const sorted = [...products].sort((a, b) => a.price - b.price);
  const cheapest = sorted[0];
  const bestSellers = [sorted[0], sorted[2], sorted[4], sorted[7]].filter(Boolean);

  const reviewsLd = (() => {    if (!reviews || reviews.length === 0) return null;
    const rated = reviews.filter((r) => r && r.rating > 0);
    if (rated.length === 0) return null;
    const avg = (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1);
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': 'https://www.party-led.com/#kit',
      name: 'Kit de bracelets LED synchronisés Party LED',
      brand: { '@type': 'Brand', name: 'Party LED' },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avg,
        reviewCount: String(rated.length),
        bestRating: '5',
        worstRating: '1',
      },
      review: rated.slice(0, 5).map((r) => ({
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5' },
        author: { '@type': 'Person', name: r.name || 'Client' },
        reviewBody: r.comment || '',
        ...(r.createdAt ? { datePublished: String(r.createdAt).slice(0, 10) } : {}),
      })),
    };
  })();

  const rc = REVIEWS_COPY[lang] || REVIEWS_COPY.fr;
  const homeReviews = [...reviews]
    .sort((a, b) => (b.rating || 0) - (a.rating || 0) || (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)))
    .slice(0, 6);
  const avgReview = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <>
        <title>{t.seo.homeTitle}</title>
        <meta name="description" content={t.seo.homeDesc} />
      </>
      {reviewsLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewsLd) }} />
      )}

      <LangSuggestBanner />

      {/* ═══════════ 1. HERO ═══════════ */}
      <section className="relative min-h-[82vh] flex items-start justify-center overflow-hidden pt-32 pb-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img src={`${BACKEND_URL}/api/uploads/hero-crowd.jpg`}
            alt="Foule portant des bracelets LED synchronisés lors d'une soirée Party LED"
            className="absolute inset-0 w-full h-full object-cover hero-kenburns" data-testid="hero-bg" />
          {showHeroVideo && <HeroVideo />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/55" />
          <div className="absolute inset-0 bg-[#050505]/30" />
        </div>
        <style>{`
          @keyframes heroKenburns { 0% { transform: scale(1.04) translate3d(0,0,0) } 100% { transform: scale(1.16) translate3d(-1.5%, -1.5%, 0) } }
          .hero-kenburns { animation: heroKenburns 26s ease-in-out infinite alternate; }
          @media (prefers-reduced-motion: reduce) { .hero-kenburns { animation: none } }
        `}</style>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-900/40 border border-purple-500/30 text-purple-200 text-xs sm:text-sm font-body tracking-wide mb-6" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> {t.hero.badge}
          </motion.span>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.8 }}
            className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.08] mb-5" data-testid="hero-headline">
            {t.hero.title1}{' '}<span className="text-purple-400">{t.hero.titleHighlight}</span>
          </motion.h1>

          <p className="text-gray-300 text-base sm:text-lg lg:text-xl font-body max-w-2xl mx-auto mb-6 leading-relaxed">
            {L.subtitle}
          </p>

          {cheapest && (
            <Link to="/catalogue" data-testid="kit-price-anchor"
              className="inline-flex items-center gap-2 bg-purple-950/60 border border-purple-500/40 hover:border-purple-400/70 text-purple-100 px-4 py-1.5 rounded-xl text-sm font-semibold mb-7 transition-colors cursor-pointer">
              {L.priceFrom} <span className="text-purple-300">{pricing.fmt(pricing.htOf(cheapest))} {L.ht}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center max-w-2xl mx-auto">
            <Link to="/devis" className="flex-1 sm:flex-none">
              <Button data-testid="hero-cta-quote"
                className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded-full px-8 py-6 text-base font-bold transition-all duration-300 hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] hover:scale-[1.02]">
                <FileText className="mr-2 w-5 h-5" /> {L.ctaQuote}
              </Button>
            </Link>
            <Link to="/catalogue" className="flex-1 sm:flex-none">
              <Button data-testid="hero-cta-catalogue"
                className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-6 text-base font-bold transition-all duration-300">
                {L.ctaCatalogue} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>

          <a href={whatsapp} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-emerald-300 hover:text-emerald-200 text-sm font-semibold transition-colors" data-testid="hero-cta-whatsapp">
            <MessageCircle className="w-4 h-4" /> {L.ctaWhatsapp}
          </a>

          <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs sm:text-sm text-gray-300 font-body">
            {L.trust.map((item, i) => {
              const Icon = trustIcons[i];
              const inner = (<><Icon className="w-4 h-4 text-purple-400 shrink-0" /> <span>{item}</span></>);
              if (i === 3) {
                return (
                  <a key={item} href="#nfc-powers" onClick={scrollToNfc} data-testid="trust-marquage-link"
                    className="flex items-center gap-2 justify-center text-center hover:text-white underline-offset-4 hover:underline transition-colors cursor-pointer">
                    {inner}
                  </a>
                );
              }
              return (
                <div key={item} className="flex items-center gap-2 justify-center text-center">{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ 2. STATS / RÉASSURANCE ═══════════ */}
      <Section className="py-10 md:py-14 px-6" id="trust">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="trust-stats-bar">
            {t.trust.stats.map((stat) => (
              <div key={stat.value} className="glass-card rounded-2xl p-6 text-center">
                <div className="font-heading text-3xl md:text-4xl font-bold text-purple-400 mb-1">{stat.value}</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider font-body">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══════════ 3. KITS LES PLUS DEMANDÉS ═══════════ */}
      {bestSellers.length > 0 && (
        <Section className="py-12 md:py-16 px-6" id="best-sellers">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{L.bestBadge}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{L.bestTitle}</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {bestSellers.map((p) => (
                <Link key={p.slug} to={`/produit/${p.slug}`} data-testid={`best-kit-${p.slug}`}
                  className="glass-card rounded-2xl p-5 group hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-white/[0.02] mb-4">
                    <img src={p.images?.[0]} alt={localizeKitName(p.name, lang)} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h3 className="font-heading text-sm sm:text-base font-bold text-white mb-1 line-clamp-2">{localizeKitName(p.name, lang)}</h3>
                  <div className="mt-auto pt-2">
                    <span className="text-gray-500 text-xs">{L.from} </span>
                    <span className="text-purple-400 font-bold">{pricing.fmt(pricing.htOf(p))} {L.ht}</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="flex justify-center mt-10">
              <Link to="/catalogue">
                <Button data-testid="best-sellers-cta"
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-6 text-base font-bold transition-all duration-300">
                  {L.bestCta} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* ═══════════ 4. COMMENT ÇA MARCHE ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative overflow-hidden" id="how-it-works">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/[0.04] rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/[0.03] rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }} />
          <div className="absolute inset-0 grid-bg opacity-40" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{t.howItWorks.badge}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{t.howItWorks.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard number={1} icon={Users} title={t.howItWorks.step1Title} description={t.howItWorks.step1Desc} delay={0} />
            <StepCard number={2} icon={Wifi} title={t.howItWorks.step2Title} description={t.howItWorks.step2Desc} delay={0.15} />
            <StepCard number={3} icon={Smartphone} title={t.howItWorks.step3Title} description={t.howItWorks.step3Desc} delay={0.3} />
          </div>
        </div>
      </Section>

      {/* ═══════════ 5. SIMULATEUR ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="simulateur">
        <div className="max-w-6xl mx-auto relative z-10"><ColorSimulator /></div>
      </Section>

      {/* ═══════════ 6. FONCTIONNALITÉS ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="features">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{t.features.badge}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{t.features.title}</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group" data-testid="feature-led">
              <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/10 rounded-full blur-[80px] group-hover:bg-purple-600/20 transition-all duration-500" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6"><Sparkles className="w-7 h-7 text-purple-400" /></div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t.features.ledTitle}</h3>
                <p className="text-gray-400 leading-relaxed font-body mb-6">{t.features.ledDesc}</p>
                <ul className="space-y-3">{t.features.ledItems.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-300 text-sm font-body"><div className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />{item}</li>
                ))}</ul>
              </div>
            </div>
            <div className="glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group" data-testid="feature-nfc">
              <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/10 rounded-full blur-[80px] group-hover:bg-purple-600/20 transition-all duration-500" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6"><Smartphone className="w-7 h-7 text-purple-400" /></div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t.features.nfcTitle}</h3>
                <p className="text-gray-400 leading-relaxed font-body mb-6">{t.features.nfcDesc}</p>
                <ul className="space-y-3">{t.features.nfcItems.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-300 text-sm font-body"><div className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />{item}</li>
                ))}</ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 6bis. BRACELET CONNECTÉ (NFC + MARQUAGE) ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative scroll-mt-24" id="nfc-powers">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{L.nfcBadge}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{L.nfcTitle}</h2>
            <p className="text-gray-400 text-base leading-relaxed font-body max-w-2xl mx-auto mt-4">{L.nfcIntro}</p>
          </div>

          {/* Exemples réels par thème + picto NFC intégré */}
          <p className="text-center text-gray-400 text-sm font-body mb-8 max-w-2xl mx-auto">{L.marquageExample}</p>
          <div className="space-y-8 mb-10" data-testid="marquage-gallery">
            {galleryThemes.map((grp) => (
              <div key={grp.key}>
                <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1.5 h-5 rounded-full bg-purple-500" />{THEME_TITLES[grp.key][titleLang] || THEME_TITLES[grp.key].fr}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {grp.items.map((ex) => (
                    <Link
                      key={ex.name}
                      to={`/devis?type=${grp.eventKey}`}
                      className="relative rounded-2xl overflow-hidden border border-white/10 bg-black group block"
                      data-testid={`marquage-item-${ex.file.replace('.webp', '')}`}
                    >
                      <img src={`${BACKEND_URL}/api/uploads/${ex.file}?v=4`} loading="lazy"
                        alt={`Bracelet LED lumineux avec marquage ${ex.name} et pictogramme NFC`}
                        className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] font-body px-2 py-1.5 text-center">{ex.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Marquage + lien offerts */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-3 bg-purple-950/50 border border-purple-500/40 rounded-full px-5 py-2.5" data-testid="marquage-offert">
              <Gift className="w-5 h-5 text-purple-300 shrink-0" />
              <span className="text-purple-100 text-sm sm:text-base font-semibold">{L.marquage}</span>
            </div>
          </div>

          {/* 4 cartes à icônes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {L.nfcItems.map((it, i) => {
              const icons = [Ticket, Star, Share2, Images, Wallet, Download];
              const Icon = icons[i];
              const highlight = i === 3;
              return (
                <div key={it.title}
                  className={`glass-card rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300 ${highlight ? 'border-purple-500/50 bg-purple-600/[0.06] sm:col-span-2 lg:col-span-1' : ''}`}
                  data-testid={`nfc-power-${i}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${highlight ? 'bg-purple-600/30 border border-purple-400/40' : 'bg-purple-600/10 border border-purple-500/20'}`}>
                    <Icon className="w-5 h-5 text-purple-300" />
                  </div>
                  <h4 className="font-heading text-base font-bold text-white mb-2">{it.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed font-body">{it.desc}</p>
                  {highlight && <Heart className="w-4 h-4 text-purple-400 mt-3" />}
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══════════ 6ter. PREUVE SOCIALE — PHOTOS RÉELLES ═══════════ */}
      {realPhotos.length > 0 && (
        <Section className="py-12 md:py-16 px-6" id="real-events">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{(SOCIAL[lang] || SOCIAL.fr).badge}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{(SOCIAL[lang] || SOCIAL.fr).title}</h2>
              <p className="text-gray-400 text-base leading-relaxed font-body max-w-2xl mx-auto mt-4">{(SOCIAL[lang] || SOCIAL.fr).intro}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="real-events-gallery">
              {realPhotos.map((p) => (
                <div key={p.id} className="relative rounded-2xl overflow-hidden border border-white/10 bg-black group aspect-square">
                  <img src={resolveMediaUrl(p.url)} loading="lazy" alt={mediaTitle(p, lang) || 'Événement équipé en bracelets LED Party LED'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {mediaTitle(p, lang) && (
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] font-body px-2 py-1.5 line-clamp-1">{mediaTitle(p, lang)}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-8">
              <Link to="/galerie" data-testid="real-events-cta">
                <Button variant="outline" className="rounded-full border-purple-500/40 text-purple-200 hover:bg-purple-600/10 px-6 py-5">
                  {(SOCIAL[lang] || SOCIAL.fr).cta}
                  <ArrowRight className="ms-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* ═══════════ 7. CAS D'USAGE ═══════════ */}
      <Section className="py-12 md:py-16 px-6" id="use-cases">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{t.useCases.badge}</span>
            <h2 className="font-heading font-bold text-white tracking-tight text-3xl sm:text-4xl lg:text-5xl">{t.useCases.title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {t.useCases.items.map((uc, i) => {
              const icons = [Heart, Music, Star, Users, GraduationCap, Building2, Rocket, Zap, Mic2, Trophy];
              return <UseCaseCard key={uc.title} icon={icons[i]} title={uc.title} description={uc.desc} delay={i * 0.05} galleryCategory={USECASE_GALLERY[i]} label={t.gallery.viewPhotos} />;
            })}
          </div>
        </div>
      </Section>

      {/* ═══════════ 8. IMMERSION ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="immersion">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden" data-testid="immersion-section">
            <img loading="lazy" src={IMMERSION_IMG} alt="Expérience immersive LED" className="w-full h-[50vh] md:h-[60vh] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
            <div className="absolute inset-0 flex items-end">
              <div className="p-8 md:p-14 max-w-2xl">
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">{t.immersion.title}</h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed font-body">{t.immersion.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 9. BÉNÉFICES ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="benefits">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{t.benefits.badge}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">{t.benefits.title}</h2>
              <p className="text-gray-400 text-base leading-relaxed font-body mb-10">{t.benefits.desc}</p>
              <div className="space-y-6">
                {t.benefits.items.map((b, i) => { const icons = [Eye, Zap, Sparkles, Shield]; return <BenefitItem key={b.title} icon={icons[i]} title={b.title} description={b.desc} />; })}
              </div>
            </div>
            <div className="relative flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-600/10 rounded-3xl blur-[60px]" />
                <img loading="lazy" src={BRACELET_IMG} alt="Bracelets LED Party LED" className="relative z-10 rounded-3xl max-w-sm w-full" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 10. AVIS (conditionnel) ═══════════ */}
      {reviews.length > 0 && (
        <Section className="py-12 md:py-16 px-6" id="avis">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{rc.badge}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{rc.title}</h2>
              {avgReview && (
                <div className="flex items-center justify-center gap-2 mt-4" data-testid="home-reviews-avg">
                  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => (<Star key={n} className={`w-5 h-5 ${n <= Math.round(avgReview) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />))}</div>
                  <span className="text-white font-semibold">{avgReview}/5</span>
                  <span className="text-gray-400 text-sm">· {reviews.length} {rc.based}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-reviews">
              {homeReviews.map((r, i) => (
                <div key={r.id || i} className="glass-card rounded-2xl p-6 flex flex-col" data-testid="home-review-card">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (<Star key={n} className={`w-4 h-4 ${n <= (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />))}</div>
                    {r.featured && <span className="text-[10px] uppercase tracking-wider text-amber-400">★ À la une</span>}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-body flex-1">« {r.comment} »</p>
                  <div className="mt-4"><p className="text-white text-sm font-semibold">{r.name}</p>{r.location ? <p className="text-purple-400 text-xs">{r.location}</p> : null}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-8">
              <Link to="/avis" data-testid="home-reviews-cta">
                <Button variant="outline" className="rounded-full border-purple-500/40 text-purple-200 hover:bg-purple-600/10 px-6 py-5">
                  {rc.leave}
                  <ArrowRight className="ms-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* ═══════════ 11. FAQ ═══════════ */}
      <Section className="py-12 md:py-16 px-6" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">{t.faq.badge}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{t.faq.title}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {t.faq.items.map((faq) => (
              <AccordionItem key={faq.q} value={faq.q} className="glass-card rounded-xl border-0 px-6 overflow-hidden">
                <AccordionTrigger className="text-white font-heading font-bold text-left hover:text-purple-400 hover:no-underline py-5 text-sm md:text-base">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-gray-400 font-body text-sm leading-relaxed pb-5">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* ═══════════ 12. CTA FINALE ═══════════ */}
      <Section className="py-14 md:py-20 px-6 relative" id="cta-final">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-600/10 rounded-full blur-[150px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-testid="final-cta">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold text-white tracking-tight mb-6">
            {t.finalCta.title1} <span className="text-purple-400">{t.finalCta.titleHighlight}</span>{t.finalCta.titleEnd}
          </h2>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed font-body mb-10 max-w-xl mx-auto">{t.finalCta.desc}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button data-testid="final-cta-quote"
                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-500 text-white rounded-full px-10 py-6 text-base font-bold transition-all duration-300 hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] hover:scale-[1.02]">
                <FileText className="mr-2 w-5 h-5" /> {L.ctaQuote}
              </Button>
            </Link>
            <Link to="/catalogue">
              <Button data-testid="final-cta-catalogue"
                className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-10 py-6 text-base font-bold transition-all duration-300">
                {L.ctaCatalogue} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          <a href={whatsapp} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 text-emerald-300 hover:text-emerald-200 text-sm font-semibold transition-colors">
            <MessageCircle className="w-4 h-4" /> {L.ctaWhatsapp}
          </a>
        </div>
      </Section>
    </div>
  );
};

export default HomeV2;
